import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Gantt, ViewMode } from 'gantt-task-react';
import moment from 'moment';
import {
    formatTaskScheduleSummary,
    formatWorkDurationHours,
    resolveGanttBarDateChange,
    durationHoursBetween,
} from '../pages/kanbanBoard/ganttHelpers';
import { mapMembersToGanttTasks } from '../utils/mapToGanttTasks';
import { TIMELINE_WORKING_HOURS } from '../config/timelineWorkingHours';
import 'gantt-task-react/dist/index.css';
import '../assets/css/gantt-task-react-chart.css';

const userFullName = (u) =>
    `${u?.first_name || ''} ${u?.last_name || ''}`.trim() || u?.email || `User ${u?.user_id}`;

const userInitials = (u) => {
    const f = (u?.first_name || '').trim();
    const l = (u?.last_name || '').trim();
    const initials = `${f[0] || ''}${l[0] || ''}`.toUpperCase();
    return initials || (u?.email || '?')[0].toUpperCase();
};

const summarizeTasks = (tasks = []) => {
    let totalHours = 0;
    let spillCount = 0;
    tasks.forEach((t) => {
        totalHours += Number(t.allocated_hours ?? t.estimated_hours ?? 0) || 0;
        if (Number(t.bandwidth_spill_hours) > 0 || t.missed_deadline) spillCount += 1;
    });
    return { taskCount: tasks.length, totalHours, spillCount };
};

export const TIMELINE_VIEW_OPTIONS = [
    { id: ViewMode.Hour, label: 'Hour' },
    { id: ViewMode.Day, label: 'Day' },
    { id: ViewMode.Week, label: 'Week' },
    { id: ViewMode.Month, label: 'Month' },
];

const PERSON_COL_PX = 220;
const HANDLE_WIDTH = 8;
const WORKING_HOUR_SPAN =
    TIMELINE_WORKING_HOURS.endHour - TIMELINE_WORKING_HOURS.startHour;

/** Same column widths as the official gantt-task-react demo. */
function columnWidthForView(view, hourFitPx) {
    if (view === ViewMode.Year) return 350;
    if (view === ViewMode.Month) return 300;
    if (view === ViewMode.Week) return 250;
    if (view === ViewMode.Hour) return hourFitPx || 65;
    return 65;
}

function columnMsForView(view) {
    if (view === ViewMode.Hour) return 3600 * 1000;
    if (view === ViewMode.Week) return 7 * 24 * 3600 * 1000;
    if (view === ViewMode.Month) return 30 * 24 * 3600 * 1000;
    if (view === ViewMode.Year) return 365 * 24 * 3600 * 1000;
    return 24 * 3600 * 1000;
}

/**
 * gantt-task-react drops edge handles when the bar is narrower than
 * handleWidth*2 (`smalltask`). Stretch short bars just enough to keep
 * start/end grips, without changing stored hours until the user resizes.
 */
function expandBarForHandles(start, end, view, columnWidth, type) {
    const actualHours = durationHoursBetween(start, end);
    if (type === 'milestone' || type === 'project') {
        return { end, displayHours: actualHours };
    }
    const minPx = HANDLE_WIDTH * 2 + 2;
    const minMs = (minPx / Math.max(columnWidth, 1)) * columnMsForView(view);
    const span = Math.max(0, end.getTime() - start.getTime());
    const visualMs = Math.max(span, minMs);
    return {
        end: new Date(start.getTime() + visualMs),
        displayHours: visualMs / 3600000,
    };
}

function cloneValidDate(value, fallbackMs) {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return new Date(value.getTime());
    }
    return new Date(fallbackMs);
}

function workingDayViewDate(fromDate, nonce = 0) {
    const d =
        fromDate instanceof Date && !Number.isNaN(fromDate.getTime())
            ? new Date(fromDate.getTime())
            : new Date();
    d.setHours(TIMELINE_WORKING_HOURS.startHour, 0, 0, Number(nonce) % 1000);
    return d;
}

/** Official demo: viewDate scrolls the axis to this instant. Stable per nonce. */
function libraryViewDate(view, nonce = 0) {
    if (view === ViewMode.Hour) return workingDayViewDate(new Date(), nonce);
    const d = new Date();
    d.setHours(12, 0, 0, Number(nonce) % 1000);
    return d;
}

function PersonListHeader({ headerHeight, fontFamily, fontSize, rowWidth }) {
    return (
        <div
            className="gantt-react-header"
            style={{ height: headerHeight, fontFamily, fontSize }}
        >
            <div className="gantt-react-header-row">
                <div
                    className="gantt-react-header-cell"
                    style={{ minWidth: rowWidth, width: rowWidth }}
                >
                    Person
                </div>
            </div>
        </div>
    );
}

function PersonListTable({
    tasks,
    rowHeight,
    rowWidth,
    fontFamily,
    fontSize,
    selectedTaskId,
    setSelectedTask,
    onExpanderClick,
    dropUserId,
    onPersonDrop,
    onBarClick,
}) {
    return (
        <div className="gantt-react-table" style={{ fontFamily, fontSize }}>
            {tasks.map((task) => {
                const isProject = task.type === 'project';
                const uid = Number(task.sourceUserId);
                const expander =
                    task.hideChildren === true ? '▶' : task.hideChildren === false ? '▼' : '';
                const member = task.sourceMember;
                const stats = member ? summarizeTasks(member.tasks) : null;
                const selected = String(selectedTaskId) === String(task.id);
                const personName =
                    task.displayName || userFullName(task.sourceUser);
                return (
                    <div
                        key={`${task.id}-row`}
                        className={`gantt-react-table-row${
                            isProject ? ' is-project' : ' is-task'
                        }${selected ? ' is-selected' : ''}${
                            isProject && Number(dropUserId) === uid ? ' is-drop-target' : ''
                        }`}
                        style={{ height: rowHeight }}
                        onDoubleClick={() => {
                            if (!isProject) onBarClick?.(task.sourceTask || task);
                        }}
                        onClick={() => setSelectedTask?.(task.id)}
                        onDragOver={
                            isProject && onPersonDrop
                                ? (e) => {
                                      e.preventDefault();
                                      onPersonDrop(
                                          e,
                                          { id: `user:${uid}`, sourceUserId: uid },
                                          'over'
                                      );
                                  }
                                : undefined
                        }
                        onDrop={
                            isProject && onPersonDrop
                                ? (e) => {
                                      e.preventDefault();
                                      onPersonDrop(
                                          e,
                                          { id: `user:${uid}`, sourceUserId: uid },
                                          'drop'
                                      );
                                  }
                                : undefined
                        }
                    >
                        <div
                            className="gantt-react-table-cell"
                            style={{ minWidth: rowWidth, maxWidth: rowWidth }}
                            title={isProject ? personName : task.name}
                        >
                            <div className="gantt-react-name-cell">
                                <button
                                    type="button"
                                    className={`gantt-react-expander${
                                        expander ? '' : ' is-empty'
                                    }`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (expander) onExpanderClick?.(task);
                                    }}
                                >
                                    {expander || '·'}
                                </button>
                                {isProject ? (
                                    <>
                                        <span className="team-row-avatar" aria-hidden>
                                            {userInitials(task.sourceUser)}
                                        </span>
                                        <span className="gantt-react-person">
                                            <strong>{personName}</strong>
                                            {stats ? (
                                                <span className="team-row-user-stats">
                                                    {stats.taskCount} task
                                                    {stats.taskCount === 1 ? '' : 's'} ·{' '}
                                                    {formatWorkDurationHours(stats.totalHours)}
                                                    {stats.spillCount > 0
                                                        ? ` · ${stats.spillCount} late`
                                                        : ''}
                                                </span>
                                            ) : null}
                                        </span>
                                    </>
                                ) : (
                                    <span className="gantt-react-task-indent">{task.name}</span>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function PinTooltip({ task, fontSize, fontFamily }) {
    const src = task.sourceTask;
    return (
        <div className="gantt-react-tooltip" style={{ fontSize, fontFamily }}>
            <div className="gantt-react-tooltip-title">
                {task.type === 'project'
                    ? task.displayName || userFullName(task.sourceUser)
                    : task.name}
            </div>
            {src ? (
                <div className="gantt-react-tooltip-meta">
                    {formatTaskScheduleSummary(src)}
                </div>
            ) : (
                <div className="gantt-react-tooltip-meta">
                    {moment(task.start).format('DD MMM HH:mm')} –{' '}
                    {moment(task.end).format('DD MMM HH:mm')}
                </div>
            )}
        </div>
    );
}

export default function GanttTaskReactChart({
    members = [],
    highlightedCardIds = null,
    previewMap = null,
    onBarClick,
    onDateChange,
    onPersonDrop,
    dropUserId = null,
    emptyMessage = 'No people with tasks in this view.',
    viewMode = ViewMode.Week,
    todayNonce = 0,
    deferCommit = false,
    onQueuePin,
}) {
    const [collapsed, setCollapsed] = useState({});
    const [chartWidth, setChartWidth] = useState(0);
    const chartRef = useRef(null);
    const libView = viewMode === 'Quarter' ? ViewMode.Month : viewMode;

    useEffect(() => {
        const el = chartRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const apply = () => setChartWidth(el.clientWidth || 0);
        apply();
        const ro = new ResizeObserver(apply);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const hourFitPx = useMemo(() => {
        const usable = Math.max(0, chartWidth - PERSON_COL_PX - 8);
        if (!usable) return 65;
        return Math.max(40, Math.floor(usable / WORKING_HOUR_SPAN));
    }, [chartWidth]);

    const columnWidth = columnWidthForView(libView, hourFitPx);

    const ganttTasks = useMemo(() => {
        const now = Date.now();
        return mapMembersToGanttTasks(members, {
            highlightedCardIds,
            previewMap,
        }).map((task) => {
            const start = cloneValidDate(task.start, now);
            let end = cloneValidDate(task.end, start.getTime() + 3600 * 1000);
            if (end.getTime() < start.getTime()) {
                end = new Date(start.getTime() + 3600 * 1000);
            }
            const visual = expandBarForHandles(start, end, libView, columnWidth, task.type);
            const next = {
                ...task,
                start,
                end: visual.end,
                displayHours: visual.displayHours,
                // Library matches with `value.id === dependencies[j]` (strict string).
                dependencies: Array.isArray(task.dependencies)
                    ? task.dependencies.map(String)
                    : undefined,
            };
            return next.type === 'project'
                ? { ...next, hideChildren: Boolean(collapsed[task.id]) }
                : next;
        });
    }, [members, highlightedCardIds, previewMap, collapsed, libView, columnWidth]);

    const viewDate = useMemo(() => {
        // Library grows/shrinks the axis from task dates. Only jump to today
        // when the user clicks Today (todayNonce > 0).
        if (!todayNonce) return undefined;
        return libraryViewDate(libView, todayNonce);
    }, [libView, todayNonce]);

    const commitPin = useCallback(
        async (task, preferred, userId, extra = {}) => {
            const payload = {
                id: `${task.id}:${userId}`,
                start: preferred instanceof Date ? preferred : new Date(preferred),
                end: extra.end || null,
                hours: extra.hours,
                isResize: Boolean(extra.isResize),
                sourceTask: task,
                sourceUserId: userId,
            };
            if (deferCommit && onQueuePin) {
                onQueuePin(payload);
                return true;
            }
            if (!onDateChange) return false;
            try {
                const result = await onDateChange(payload);
                return result !== false;
            } catch {
                return false;
            }
        },
        [deferCommit, onQueuePin, onDateChange]
    );

    const handleDateChange = useCallback(
        async (ganttTask) => {
            // Official demo: update ONLY this task. The 2nd arg is barChildren
            // (successors that list this id in `dependencies`) — representational
            // arrows, not a cascade. Do not pin or move them.
            if (!ganttTask || ganttTask.type === 'project' || ganttTask.isDisabled) {
                return false;
            }
            if (!onDateChange && !onQueuePin) return false;

            let source = ganttTask.sourceTask;
            let userId = Number(ganttTask.sourceUserId);
            if (!source) {
                const parts = String(ganttTask.id || '').split(':');
                const cardId = Number(parts[0]);
                userId = userId || Number(parts[1]);
                for (const member of members || []) {
                    if (userId && Number(member.user?.user_id) !== userId) continue;
                    const found = (member.tasks || []).find((t) => Number(t.id) === cardId);
                    if (found) {
                        source = found;
                        userId = userId || Number(member.user?.user_id);
                        break;
                    }
                }
            }
            if (!source || !userId) {
                if (process.env.NODE_ENV !== 'production') {
                    // eslint-disable-next-line no-console
                    console.debug('[gantt] onDateChange aborted — missing source/user', {
                        id: ganttTask.id,
                    });
                }
                return false;
            }
            const resolved = resolveGanttBarDateChange(ganttTask, source);
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.debug('[gantt] onDateChange', {
                    name: source.title || ganttTask.name,
                    start: resolved.start,
                    end: resolved.end,
                    hours: resolved.hours,
                    isResize: resolved.isResize,
                });
            }
            return commitPin(source, resolved.start, userId, {
                end: resolved.end,
                hours: resolved.hours,
                isResize: resolved.isResize,
            });
        },
        [commitPin, members, onDateChange, onQueuePin]
    );

    const handleExpanderClick = useCallback((task) => {
        if (task?.type !== 'project') return;
        setCollapsed((prev) => ({ ...prev, [task.id]: !prev[task.id] }));
    }, []);

    const TaskListTable = useCallback(
        (props) => (
            <PersonListTable
                {...props}
                dropUserId={dropUserId}
                onPersonDrop={onPersonDrop}
                onBarClick={onBarClick}
            />
        ),
        [dropUserId, onPersonDrop, onBarClick]
    );

    const canPin = Boolean(onDateChange || onQueuePin);
    const empty = !members.length || !ganttTasks.length;

    return (
        <div className="tl-chart-shell gantt-react-shell">
            <div ref={chartRef} className="gantt-react-chart">
                {empty ? (
                    <p className="text-muted small gantt-react-empty">{emptyMessage}</p>
                ) : (
                <Gantt
                    key={libView}
                    tasks={ganttTasks}
                    viewMode={libView}
                    viewDate={viewDate}
                    preStepsCount={1}
                    locale="en-GB"
                    columnWidth={columnWidth}
                    listCellWidth={`${PERSON_COL_PX}px`}
                    rowHeight={50}
                    headerHeight={50}
                    ganttHeight={520}
                    barFill={60}
                    barCornerRadius={3}
                    handleWidth={HANDLE_WIDTH}
                    timeStep={300000}
                    fontFamily="Arial, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue"
                    fontSize="14px"
                    todayColor="rgba(252, 248, 227, 0.5)"
                    arrowColor="grey"
                    arrowIndent={20}
                    TooltipContent={PinTooltip}
                    TaskListHeader={PersonListHeader}
                    TaskListTable={TaskListTable}
                    onDateChange={canPin ? handleDateChange : undefined}
                    onDelete={() => false}
                    onDoubleClick={(task) => {
                        if (task?.type === 'task' || task?.type === 'milestone') {
                            onBarClick?.(task.sourceTask || task);
                        }
                    }}
                    onExpanderClick={handleExpanderClick}
                />
                )}
            </div>
        </div>
    );
}

export { ViewMode, PERSON_COL_PX };
